import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check, Plug, ShieldCheck, RefreshCcw, Terminal, ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { PRIMARY, Reveal, Kicker } from "@/components/landing/shared";

// Public "connect quickly" guide (Phase 12) — the low-friction deliverable: paste one URL into
// Claude's connector settings, authorize once, done. Static JSX text only (stored-XSS invariant).
const CONNECTOR_URL = `${window.location.origin}/api/mcp`;

const STEPS = [
  { i: Plug, t: "Add a custom connector", b: "In Claude — on the web (claude.ai) or in the desktop app — open Settings → Connectors and choose “Add custom connector.”" },
  { i: Copy, t: "Paste the Runebox URL", b: "Paste the connector URL below, then click Connect. Claude opens a Runebox approval page." },
  { i: ShieldCheck, t: "Approve once", b: "You’re already signed in to Runebox, so it’s a single “Allow.” Your approved skills, agents, and commands appear in Claude — always the latest published version." },
];

export default function ConnectClaude() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const startTeam = () => navigate(user ? "/orgs/new" : "/signup?redirect=/orgs/new");
  const goLogin = () => navigate("/login");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CONNECTOR_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked — the URL is right there to select */ }
  };

  return (
    <div className="min-h-screen bg-[color:var(--rb-void)]">
      <LandingNav user={user} login={goLogin} startTeam={startTeam} navigate={navigate} />

      <main id="top" data-testid="connect-claude">
        <section className="rb-grain relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(680px circle at 50% -6%, rgba(45,212,191,0.12), transparent 55%)" }} />
          <div className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-20">
            <Reveal>
              <div className="text-center">
                <Kicker><Sparkles className="h-3.5 w-3.5" /> Claude.ai &amp; Claude Desktop</Kicker>
                <h1 className="rb-display mx-auto max-w-2xl text-4xl font-semibold leading-[1.08] tracking-tight text-[color:var(--rb-text)] sm:text-5xl">
                  Add Runebox to <span className="text-[color:var(--rb-accent)]">Claude</span>
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[color:var(--rb-text-dim)]">
                  Connect once and your governed Runebox catalog shows up right inside Claude on the web and desktop &mdash; always the latest approved version. No downloads, no re-uploading.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-8 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-5">
                <div className="text-xs font-medium uppercase tracking-wide text-[color:var(--rb-text-faint)]">Connector URL</div>
                <div className="mt-2 flex items-center gap-2">
                  <code className="flex-1 truncate rounded-lg border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] px-3 py-2.5 rb-mono text-sm text-[color:var(--rb-accent)]" data-testid="connector-url">{CONNECTOR_URL}</code>
                  <Button onClick={copy} variant="outline" data-testid="copy-connector-url"
                    className="h-10 shrink-0 rounded-lg border-[color:var(--rb-border)] text-[color:var(--rb-text)] hover:bg-[color:var(--rb-surface)]">
                    {copied ? <><Check className="mr-1.5 h-4 w-4 text-[color:var(--rb-accent)]" /> Copied</> : <><Copy className="mr-1.5 h-4 w-4" /> Copy</>}
                  </Button>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="pb-4">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div className="space-y-4">
              {STEPS.map(({ i: Icon, t, b }, idx) => (
                <Reveal key={t} delay={idx * 0.06}>
                  <div className="flex items-start gap-4 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-6">
                    <div className="flex items-center gap-3">
                      <span className="rb-mono text-2xl font-semibold text-[color:rgba(94,234,212,0.28)]">{idx + 1}</span>
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)]">
                        <Icon className="h-5 w-5 text-[color:var(--rb-accent)]" />
                      </div>
                    </div>
                    <div>
                      <h3 className="rb-display text-lg font-semibold text-[color:var(--rb-text)]">{t}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--rb-text-dim)]">{b}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-6">
              <h3 className="rb-display text-sm font-semibold text-[color:var(--rb-text)]">Good to know</h3>
              <ul className="mt-3 space-y-2 text-sm text-[color:var(--rb-text-dim)]">
                <li>&bull; The connector is <b className="text-[color:var(--rb-text)]">read-only</b> and always serves your latest <b className="text-[color:var(--rb-text)]">approved</b> catalog &mdash; the same governance as the Claude Code plugin.</li>
                <li>&bull; Claude can <b className="text-[color:var(--rb-text)]">find and load a skill on its own</b> mid-chat (ask it to list your Runebox skills), or you can pull one in from the connector menu.</li>
                <li>&bull; It shows the artifacts you can already access: your org&rsquo;s published catalog, your own approved skills, and anything you&rsquo;ve purchased.</li>
                <li>&bull; Want a skill <b className="text-[color:var(--rb-text)]">always on</b> (auto-activated, no connector)? Upload it under Claude&rsquo;s Skills feature &mdash; see <button onClick={() => navigate("/use-with-claude")} className="text-[color:var(--rb-accent)] underline">the Skills upload guide</button>.</li>
                <li>&bull; Free Claude accounts allow one custom connector; Pro/Max/Team/Enterprise allow more.</li>
              </ul>
              <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)] p-3 text-sm text-[color:var(--rb-text-dim)]">
                <Terminal className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--rb-accent)]" />
                <span><b className="text-[color:var(--rb-text)]">Advanced (Desktop):</b> point any remote-MCP client at <span className="rb-mono text-[color:var(--rb-accent)]">{CONNECTOR_URL}</span> with an <span className="rb-mono">Authorization: Bearer rbx_&hellip;</span> header using your <button onClick={() => navigate("/profile")} className="text-[color:var(--rb-accent)] underline">API key</button> &mdash; no OAuth needed.</span>
              </div>
              <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[color:rgba(45,212,191,0.3)] bg-[color:rgba(45,212,191,0.08)] p-3 text-sm text-[color:var(--rb-accent)]">
                <RefreshCcw className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Using Claude <b>Code</b>? Use the plugin instead &mdash; <span className="rb-mono">/runebox:sync</span> installs your whole catalog automatically.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-20">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <Button onClick={() => navigate(user ? "/my-skills" : "/marketplace")} className={`h-12 rounded-xl px-7 text-base ${PRIMARY}`} data-testid="connect-cta">
              {user ? "Go to My Artifacts" : "Browse the free gallery"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </section>
      </main>

      <LandingFooter login={goLogin} />
    </div>
  );
}
